import type { Tracker } from '@archipro-website/tracker';
import { readEmailAttribution } from './emailAttributionCookie';
import type { BookMeetingRevealSource } from './trackBookMeetingReveal';

export interface TrackBookMeetingSubmitProps {
    /** The `Source` the opening's `BookMeetingReveal` reported. */
    source?: BookMeetingRevealSource;
    /**
     * The professional the meeting is with. Required for the meeting to count
     * towards that client's advertising conversions rather than only site-wide
     * ones.
     */
    professionalId?: number;
    /** The page item the Book button sat on. */
    libraryItemId: number;
    /** Booked straight into a calendar, rather than sent as a request. */
    booked: boolean;
}

/**
 * Track the `BookMeetingSubmit` event: the booking gate sent a meeting request
 * or booked a meeting. It is a lead for the advertising platforms, with the
 * weight of an enquiry, but it is not an enquiry and reports none of the
 * enquiry events.
 *
 * The warehouse counts meetings from the server's `book_meeting_*` events, so
 * this browser copy exists for the advertising platforms and is not itself a
 * counted conversion.
 *
 * `ExtraData` carries whether the meeting was booked, and the email campaign
 * that drove it in `attribution`, as `CreatedEnquiry` does.
 */
export const trackBookMeetingSubmit = (
    tracker: Tracker,
    props: TrackBookMeetingSubmitProps,
) => {
    const attribution = readEmailAttribution();
    const extraData = {
        booked: props.booked,
        ...(attribution && {
            attribution: attribution.source,
            ...(attribution.submissionId && {
                attributionSubmissionId: attribution.submissionId,
            }),
        }),
    };
    void tracker.log('BookMeetingSubmit', {
        url: new URL(window.location.href),
        professionalId: props.professionalId,
        data: {
            Source: props.source,
            ProfessionalID: props.professionalId,
            LibraryItemID: props.libraryItemId,
            ExtraData: JSON.stringify(extraData),
        },
    });
};
